import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, User, Mail, Phone, Lock } from "lucide-react";
import { toast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import Loader from "@/components/ui/loader";

const RegisterFormContent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: ''
  });
  
  const { signUp } = useAuth();

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!registerData.firstName.trim()) errors.push("Il nome è obbligatorio");
    if (!registerData.lastName.trim()) errors.push("Il cognome è obbligatorio");
    if (!registerData.email.trim()) errors.push("L'email è obbligatoria");
    if (!registerData.phone.trim()) errors.push("Il numero di telefono è obbligatorio");
    if (!registerData.password || registerData.password.length < 6) {
      errors.push("La password deve essere di almeno 6 caratteri");
    }


    if (errors.length > 0) {
      toast({
        title: "Errore di Validazione",
        description: errors.join(", "),
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const metadata = {
        first_name: registerData.firstName,
        last_name: registerData.lastName,
        phone: registerData.phone,
        role: 'travel_designer'
      };
      
      const { error } = await signUp(registerData.email, registerData.password, metadata);
      
      if (error) {
        let errorMessage = "Registrazione fallita. Riprova.";
        
        if (error.message.includes("User already registered")) {
          errorMessage = "Un account con questa email esiste già. Prova ad accedere.";
        } else if (error.message.includes("Password should be at least")) {
          errorMessage = "La password è troppo debole. Usa una password più forte.";
        } else if (error.message.includes("Invalid email")) {
          errorMessage = "Inserisci un indirizzo email valido.";
        }
        
        toast({
          title: "Registrazione Fallita",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Registrazione Completata! 📧",
          description: "Controlla la tua email per verificare l'account.",
        });
        setRegisterData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: ''
        });
      }
    } catch (err) {
      toast({
        title: "Errore di Registrazione",
        description: "Si è verificato un errore imprevisto. Riprova.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleRegister} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">Nome</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              id="firstName" 
              value={registerData.firstName}
              onChange={(e) => setRegisterData({...registerData, firstName: e.target.value})}
              className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
              placeholder="Il tuo nome"
              required 
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">Cognome</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              id="lastName" 
              value={registerData.lastName}
              onChange={(e) => setRegisterData({...registerData, lastName: e.target.value})}
              className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
              placeholder="Il tuo cognome"
              required 
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="registerEmail" className="text-sm font-medium text-gray-700">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            id="registerEmail" 
            type="email" 
            value={registerData.email}
            onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
            className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
            placeholder="nome@esempio.com"
            required 
            disabled={isLoading}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Telefono</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            id="phone" 
            type="tel" 
            value={registerData.phone}
            onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
            className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
            placeholder="+39 123 456 7890"
            required 
            disabled={isLoading}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="registerPassword" className="text-sm font-medium text-gray-700">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            id="registerPassword" 
            type="password" 
            value={registerData.password}
            onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
            className="h-10 pl-10 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
            placeholder="Crea una password sicura"
            required 
            disabled={isLoading}
            minLength={6}
          />
        </div>
      </div>

      
      <Button 
        type="submit" 
        className="w-full bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium transition-colors duration-200"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader size="sm" className="mr-3" />
            Registrazione in corso...
          </>
        ) : (
          <>
            <UserPlus className="w-5 h-5 mr-2" />
            Registrati
          </>
        )}
      </Button>
    </form>
  );
};

export default RegisterFormContent; 